import React from "react";
import _ from "underscore";

import {
  makeStyles,
  withStyles,
  useMediaQuery,
  Button,
  Chip,
  Dialog,
  Fab,
  FormControl,
  Input,
  InputAdornment,
  InputLabel,
  MenuItem,
  Table,
  TableCell,
  TableRow,
  TableBody,
  TableHead,
  TableSortLabel,
  TablePagination,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Select,
  Collapse,
  Box,
} from "@material-ui/core";

import { useTheme } from "@material-ui/core/styles";
import { ArrowUpward, Close, Search, Delete, DonutLarge, FilterList, ExpandMore, ExpandLess, QueueMusic, Equalizer, ChevronLeft, ChevronRight } from "@material-ui/icons";
import Spotify from "spotify-web-api-js";

import { initializeApp } from "firebase/app";
import { firebaseConfig } from "../../../src/config/firebaseConfig";

import KeyMap from "../../utils/KeyMap";
import { camelotToKeyMode } from "../../utils/harmonic";
import { releaseSortKey, releaseYear } from "../../utils/release";
import { useEffect } from "react";

import Row from "./Row";
import Recommendations from "./Recommendations";
import KeyFilterPicker from "./KeyFilterPicker";
import CrateDNA from "./CrateDNA";
import { useSpotifyChords } from "../../utils/useSpotifyChords";
import { useOverrides } from "../../utils/useOverrides";

initializeApp(firebaseConfig);

const musicalKeys = [
  "C",
  "C♯/D♭",
  "D",
  "D♯/E♭",
  "E",
  "F",
  "F♯/G♭",
  "G",
  "G♯/A♭",
  "A",
  "A♯/B♭",
  "B",
];

const camelotKeys = [
  "1A",
  "1B",
  "2A",
  "2B",
  "3A",
  "3B",
  "4A",
  "4B",
  "5A",
  "5B",
  "6A",
  "6B",
  "7A",
  "7B",
  "8A",
  "8B",
  "9A",
  "9B",
  "10A",
  "10B",
  "11A",
  "11B",
  "12A",
  "12B",
];

const openKeys = [
  "1d",
  "1m",
  "2d",
  "2m",
  "3d",
  "3m",
  "4d",
  "4m",
  "5d",
  "5m",
  "6d",
  "6m",
  "7d",
  "7m",
  "8d",
  "8m",
  "9d",
  "9m",
  "10d",
  "10m",
  "11d",
  "11m",
  "12d",
  "12m",
];

const useStyles = makeStyles((theme) => ({
  appBar: {
    position: "sticky",
    backgroundColor: "#191414",
  },
  title: {
    flex: 0,
    marginLeft: theme.spacing(3),
  },
  search: {
    flex: 1,
    color: "white",
    marginRight: theme.spacing(3),
    marginLeft: theme.spacing(3),
    borderWidth: "10px",
    [theme.breakpoints.down('sm')]: {
      marginRight: 0,
      marginLeft: 0,
    },
  },
  input: {
    color: "white",
  },
  filter: {
    marginLeft: theme.spacing(3),
    marginBottom: theme.spacing(1),
    minWidth: 120,
    maxWidth: 300,
    [theme.breakpoints.down('sm')]: {
      marginLeft: 0,
      marginBottom: theme.spacing(0.5),
      minWidth: '100%',
      maxWidth: '100%',
      fontSize: '0.875rem',
      '& .MuiInputLabel-root': {
        fontSize: '0.75rem',
      },
      '& .MuiSelect-root': {
        fontSize: '0.875rem',
      },
    },
  },
  minFilter: {
    marginLeft: theme.spacing(5),
    marginBottom: theme.spacing(1),
    minWidth: 20,
    maxWidth: 50,
    [theme.breakpoints.down('sm')]: {
      marginLeft: theme.spacing(0.5),
      marginBottom: theme.spacing(0.5),
      minWidth: 60,
      maxWidth: 60,
      '& .MuiInputLabel-root': {
        fontSize: '0.75rem',
      },
      '& .MuiInput-root': {
        fontSize: '0.875rem',
      },
    },
  },
  toFilter: {
    marginLeft: theme.spacing(1),
    marginBottom: theme.spacing(1),
    minWidth: 20,
    maxWidth: 20,
    [theme.breakpoints.down('sm')]: {
      marginLeft: theme.spacing(0.5),
      marginBottom: theme.spacing(0.5),
      '& .MuiInputLabel-root': {
        fontSize: '0.75rem',
      },
    },
  },
  maxFilter: {
    marginLeft: theme.spacing(1),
    marginBottom: theme.spacing(1),
    minWidth: 20,
    maxWidth: 50,
    [theme.breakpoints.down('sm')]: {
      marginLeft: theme.spacing(0.5),
      marginBottom: theme.spacing(0.5),
      minWidth: 60,
      maxWidth: 60,
      '& .MuiInputLabel-root': {
        fontSize: '0.75rem',
      },
      '& .MuiInput-root': {
        fontSize: '0.875rem',
      },
    },
  },
  chips: {
    display: "flex",
    flexWrap: "wrap",
  },
  chip: {
    margin: 2,
    [theme.breakpoints.down('sm')]: {
      height: 24,
      fontSize: '0.75rem',
      '& .MuiChip-label': {
        padding: '0 8px',
      },
    },
  },
  noLabel: {
    marginTop: theme.spacing(3),
  },
  icon: {
    fill: "white",
  },
  root: {
    fill: "white",
    color: "white",
  },

  button: {
    margin: theme.spacing(1),
    color: "white",
  },
}));

const StyledTableCell = withStyles((theme) => ({
  head: {
    backgroundColor: "#1ED760",
    color: theme.palette.common.white,
    fontWeight: "bold",
  },
  body: {
    fontSize: 14,
  },
}))(TableCell);

// Sort arrows on the green header need to stay white (and visible) instead of
// MUI's default dark/secondary text color.
const HeadSortLabel = withStyles({
  root: {
    color: "inherit",
    "&:hover": { color: "#fff" },
    "&:focus": { color: "#fff" },
    "&$active": { color: "#fff" },
  },
  active: {},
  icon: { color: "inherit !important" },
})(TableSortLabel);

let Playlist = (props) => {
  const classes = useStyles();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("xl"));
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const allItems = props.playlist;

  // Spotify chord loops (computed client-side from /audio-analysis, cached).
  // SoundCloud chords come from props.chordsById; Spotify ones from here.
  const spotifyChordsById = useSpotifyChords(allItems, props.token);

  // Per-track manual key/chord overrides (cross-device, survive re-analysis).
  const allItemIds = React.useMemo(
    () => (allItems || []).map((it) => it.track && it.track.id).filter(Boolean),
    [allItems]
  );
  const { overridesById, setOverride } = useOverrides(allItemIds);

  const [search, setSearch] = React.useState("");
  const [wheel, setWheel] = React.useState("Musical");
  const [keyFilter, setKeyFilter] = React.useState([]);
  const [minBpm, setMinBpm] = React.useState("");
  const [maxBpm, setMaxBpm] = React.useState("");
  const [minYear, setMinYear] = React.useState("");
  const [maxYear, setMaxYear] = React.useState("");
  const [sortBy, setSortBy] = React.useState("key");
  const [sortDir, setSortDir] = React.useState("asc");
  const [energyFilter, setEnergyFilter] = React.useState("any");
  const [showDNA, setShowDNA] = React.useState(false);
  const [showFilters, setShowFilters] = React.useState(!isMobile); // Collapsed on mobile by default
  let [searchItems, setSearchItems] = React.useState(allItems);
  // Track whose key is "anchored" for harmonic-mixing highlighting (or null).
  const [harmonicAnchorId, setHarmonicAnchorId] = React.useState(null);
  // Key-filter bottom-sheet open state.
  const [pickerOpen, setPickerOpen] = React.useState(false);
  // Saved picker style: "notation" (Piano/Wheel by notation) or "combined".
  const [filterMode, setFilterMode] = React.useState(
    window.localStorage.getItem("keytrack_filter_mode") === "combined"
      ? "combined"
      : "notation"
  );

  const changeFilterMode = (mode) => {
    window.localStorage.setItem("keytrack_filter_mode", mode);
    setFilterMode(mode);
  };

  // Every picker (piano, wheel, combined) reads/writes the filter as Camelot
  // codes, so the selection is independent of the notation being shown.
  const toggleKeyFilter = (code) => {
    setKeyFilter((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );
  };

  let topRef = React.createRef();

  let handleChange = _.debounce((event) => {
    event.persist();
    setSearch(String(event.target.value).toLowerCase());
  }, 500);

  // useCallback so Row (memoized) keeps a stable reference and doesn't
  // re-render every time Playlist re-renders for unrelated reasons.
  const handleRowClick = React.useCallback(
    (event, item) => {
      // Combined-view only: SoundCloud rows play through the Widget bar, never
      // the Spotify Web Playback player. Spotify / no-__source rows are
      // untouched and fall through to the existing updatePlayer call.
      if (item.__source === "soundcloud") {
        if (props.onPlaySoundcloud) props.onPlaySoundcloud(item.__scRaw);
        return;
      }
      let uri = item.track.uri;
      if (props.updatePlayer) {
        props.updatePlayer([uri], true);
      }
    },
    [props.updatePlayer, props.onPlaySoundcloud]
  );

  const spotifyWebApi = new Spotify();
  spotifyWebApi.setAccessToken(props.token);

  // Index audio features by track id once, so getKey is O(1). Previously it
  // did a linear .find() per call, and the table sort calls getKey twice per
  // comparison — O(n^2 log n), which froze the UI on large crates for seconds.
  const keysById = React.useMemo(() => {
    const map = new Map();
    (props.playlistKeys || []).forEach((track) => {
      if (track) map.set(track.id, track);
    });
    return map;
  }, [props.playlistKeys]);

  const getKey = React.useCallback(
    (id) => {
      if (!id) return undefined;
      const result = keysById.get(id);
      let base = result
        ? {
            key: result.key,
            mode: result.mode,
            bpm: result.tempo,
            energy: result.energy,
            danceability: result.danceability,
            valence: result.valence,
          }
        : null;
      // A manual key override (a Camelot code) replaces the detected key/mode
      // everywhere getKey feeds — the pill, color, harmonic matching, filtering.
      const ov = overridesById[id];
      if (ov && ov.keyOverride) {
        const km = camelotToKeyMode(ov.keyOverride);
        if (km) {
          base = base || { bpm: null, energy: null, danceability: null, valence: null };
          base = { ...base, key: km.key, mode: km.mode };
        }
      }
      return base;
    },
    [keysById, overridesById]
  );

  // Camelot code of the anchored track, derived from its key.
  const anchorKey = harmonicAnchorId ? getKey(harmonicAnchorId) : null;
  const harmonicAnchorCamelot =
    anchorKey && KeyMap[anchorKey.key]
      ? KeyMap[anchorKey.key].camelot[anchorKey.mode]
      : null;

  const toggleHarmonicAnchor = React.useCallback((item) => {
    setHarmonicAnchorId((prev) =>
      prev === item.track.id ? null : item.track.id
    );
  }, []);

  // Add a track to the app-level set, tagging it with this crate's key/BPM so
  // the set stays self-contained across playlists.
  const handleAddToSet = React.useCallback(
    (item) => {
      if (props.onAddToSet) props.onAddToSet(item, getKey(item.track.id));
    },
    [props.onAddToSet, getKey]
  );

  // Pagination keeps the rendered DOM small. A huge table (thousands of nodes)
  // makes the browser re-layout the whole page whenever a dialog/drawer opens,
  // which froze opening the Set/Calculator for seconds on large crates.
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(100);

  // Sort once per data change (not on every render), on a copy so we don't
  // mutate the searchItems state in place. `sortBy` is a column id and
  // `sortDir` is "asc"/"desc"; tracks missing audio features always sort last.
  const sortedItems = React.useMemo(() => {
    const dir = sortDir === "desc" ? -1 : 1;
    const arr = [...searchItems];
    arr.sort((a, b) => {
      // Columns that don't need audio features.
      if (sortBy === "released")
        return dir * (releaseSortKey(a.track) - releaseSortKey(b.track));
      if (sortBy === "track")
        return dir * a.track.name.localeCompare(b.track.name);
      if (sortBy === "artist") {
        const an = a.track.artists[0] ? a.track.artists[0].name : "";
        const bn = b.track.artists[0] ? b.track.artists[0].name : "";
        return dir * an.localeCompare(bn);
      }
      // Audio-feature columns: keep tracks with no key/BPM at the bottom
      // regardless of sort direction.
      const aKey = getKey(a.track.id);
      const bKey = getKey(b.track.id);
      if (!aKey && !bKey) return 0;
      if (!aKey) return 1;
      if (!bKey) return -1;
      if (sortBy === "bpm") return dir * (aKey.bpm - bKey.bpm);
      if (sortBy === "energy")
        return dir * ((aKey.energy || 0) - (bKey.energy || 0));
      if (sortBy === "danceability")
        return dir * ((aKey.danceability || 0) - (bKey.danceability || 0));
      if (sortBy === "valence")
        return dir * ((aKey.valence || 0) - (bKey.valence || 0));
      // Default ("key"): Camelot code, then BPM as a tiebreaker. A track with
      // no valid key (e.g. Spotify key === -1) sorts last rather than crashing.
      const aMap = KeyMap[aKey.key];
      const bMap = KeyMap[bKey.key];
      if (!aMap && !bMap) return 0;
      if (!aMap) return 1;
      if (!bMap) return -1;
      const aCamelot = aMap.camelot[aKey.mode];
      const bCamelot = bMap.camelot[bKey.mode];
      const cmp = aCamelot.localeCompare(bCamelot);
      if (cmp !== 0) return dir * cmp;
      return dir * (aKey.bpm - bKey.bpm);
    });
    return arr;
  }, [searchItems, getKey, sortBy, sortDir]);

  // Sensible default direction the first time a column is selected. Energy/
  // vibe and "newest" are most useful high-to-low; names and keys low-to-high.
  const defaultSortDir = (col) =>
    ["energy", "danceability", "valence", "released"].includes(col)
      ? "desc"
      : "asc";

  // Clicking a column header sorts by it; clicking the active column flips
  // direction. Shared with the "Sort by" dropdown so the two stay in sync.
  const handleSort = (col) => {
    if (sortBy === col) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(col);
      setSortDir(defaultSortDir(col));
    }
  };

  // Only the current page is rendered into the DOM.
  const pagedItems = React.useMemo(
    () => sortedItems.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sortedItems, page, rowsPerPage]
  );

  // Reset to the first page only on USER-driven changes (search, key/BPM/Year/
  // energy filters, sort) — NOT when `sortedItems` merely re-fills as live
  // SoundCloud analysis resolves keys. Depending on `sortedItems` reset the
  // page on every analysis completion, yanking the user back to page 1 mid-run.
  React.useEffect(() => {
    setPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, keyFilter, minBpm, maxBpm, minYear, maxYear, energyFilter, sortBy, sortDir]);

  // Safety net: if the current page falls past the end of the result set (e.g.
  // a filter shrank it), clamp back into range instead of showing a blank page.
  // This does NOT force page 0, so live data fills don't disturb the page.
  React.useEffect(() => {
    const lastPage = Math.max(0, Math.ceil(sortedItems.length / rowsPerPage) - 1);
    if (page > lastPage) setPage(lastPage);
  }, [sortedItems.length, rowsPerPage, page]);

  // Scroll back to the top of the table — used by the "Back to Top" Fab only.
  // `block: "center"` clears the sticky filter AppBar.
  const scrollToTop = () => {
    if (topRef.current)
      topRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const pageCount = Math.max(1, Math.ceil(sortedItems.length / rowsPerPage));

  useEffect(() => {
    if (
      keyFilter.length === 0 &&
      search === "" &&
      minBpm === "" &&
      maxBpm === "" &&
      minYear === "" &&
      maxYear === "" &&
      energyFilter === "any"
    ) {
      _.debounce(setSearchItems(allItems), 500);
    } else {
      _.debounce(
        setSearchItems((searchItems) => {
          let filteredItems = allItems;

          if (search !== "") {
            filteredItems = filteredItems.filter((item) => {
              let artists = item.track.artists
                .map((artist) => artist.name)
                .join(", ")
                .toLowerCase();

              let trackName = String(item.track.name).toLowerCase();
              return artists.includes(search) || trackName.includes(search);
            });
          }
          if (keyFilter.length !== 0) {
            filteredItems = filteredItems.filter((item) => {
              let trackKey = getKey(item.track.id);
              if (!trackKey || !KeyMap[trackKey.key]) return false;
              // keyFilter holds Camelot codes (set via the wheel).
              let camelot = KeyMap[trackKey.key].camelot[trackKey.mode];
              return keyFilter.includes(camelot);
            });
          }
          if (minBpm !== "") {
            let bpmNum = parseInt(minBpm);
            filteredItems = filteredItems.filter((item) => {
              let trackKey = getKey(item.track.id);
              let mappedBpm =
                (trackKey || trackKey === 0) && Math.round(trackKey.bpm);

              return mappedBpm >= bpmNum;
            });
          }

          if (maxBpm !== "") {
            let bpmNum = parseInt(maxBpm);
            filteredItems = filteredItems.filter((item) => {
              let trackKey = getKey(item.track.id);
              let mappedBpm =
                (trackKey || trackKey === 0) && Math.round(trackKey.bpm);

              return mappedBpm <= bpmNum;
            });
          }

          if (minYear !== "") {
            const y = parseInt(minYear, 10);
            filteredItems = filteredItems.filter((item) => {
              const ry = releaseYear(item.track);
              return ry !== null && ry >= y;
            });
          }
          if (maxYear !== "") {
            const y = parseInt(maxYear, 10);
            filteredItems = filteredItems.filter((item) => {
              const ry = releaseYear(item.track);
              return ry !== null && ry <= y;
            });
          }
          if (energyFilter !== "any") {
            filteredItems = filteredItems.filter((item) => {
              const tk = getKey(item.track.id);
              const e = tk ? tk.energy : null;
              if (e == null) return false;
              if (energyFilter === "low") return e < 0.4;
              if (energyFilter === "med") return e >= 0.4 && e <= 0.7;
              return e > 0.7;
            });
          }
          return filteredItems;
        }, 500)
      );
    }
    // `wheel` only changes the displayed notation, not which tracks match.
  }, [search, keyFilter, minBpm, maxBpm, minYear, maxYear, energyFilter]);

  const handleFilterChange = (event, type) => {
    const {
      target: { value },
    } = event;

    const setValue = value;

    let funcMap = {
      wheel: setWheel,
      minBpm: _.debounce(setMinBpm, 500),
      maxBpm: _.debounce(setMaxBpm, 500),
      minYear: _.debounce(setMinYear, 500),
      maxYear: _.debounce(setMaxYear, 500),
    };

    funcMap[type](setValue);
  };

  const clearFilters = () => {
    setKeyFilter([]);
    setMinBpm("");
    setMaxBpm("");
    setMinYear("");
    setMaxYear("");
    setEnergyFilter("any");
    ["minBpm", "maxBpm", "minYear", "maxYear"].forEach((id) => {
      const el = document.getElementById(id);
      if (el) el.value = "";
    });
  };

  // TODO: Abstract this to new component

  // Combined-view header tint. `__source` is only set in the combined adapter,
  // so for a pure-Spotify playlist hasSC is false and headerBg stays null —
  // every header cell keeps its green `head` class and the row gets no bg
  // (i.e. the Spotify-only path is byte-for-byte unchanged). With SoundCloud
  // present: a diagonal green/orange split when Spotify is also there, else
  // solid orange for an all-SoundCloud crate.
  const hasSC = (props.playlist || []).some((i) => i.__source === "soundcloud");
  const hasSP = (props.playlist || []).some((i) => i.__source !== "soundcloud");
  const headerBg = !hasSC
    ? null
    : hasSP
    ? "linear-gradient(115deg, #1ED760 0%, #1ED760 calc(50% - 1px), #fff calc(50% - 1px), #fff calc(50% + 1px), #ff5500 calc(50% + 1px), #ff5500 100%)"
    : "#ff5500";
  // When a header tint is active, make every cell transparent so the row's
  // color/gradient shows through continuously; otherwise (Spotify-only) leave
  // the cells alone so they keep their green `head` background. White bold text
  // is unchanged either way (it comes from StyledTableCell's `head` class).
  const headCellStyle = headerBg ? { backgroundColor: "transparent" } : undefined;

  // SoundCloud has no energy/danceability/valence audio features, so an all-SC
  // view hides the Energy column + filter + those sort options. The full-width
  // "Back to Top" bar follows the same accent as the header: green (Spotify),
  // orange (all-SC), or the green→orange split (mixed).
  const hideEnergy = !hasSP;
  const backToTopBg = headerBg || "#1ED760";

  return (
    <div className="m-div">
      <Dialog
        fullScreen={fullScreen}
        open={props.open}
        onClose={props.handlePlaylistClose}
      >
        <AppBar className={classes.appBar}>
          <Toolbar style={{ 
            minHeight: isMobile ? '56px' : '64px',
            padding: isMobile ? theme.spacing(0, 1) : theme.spacing(0, 2)
          }}>
            {isMobile && (
              <IconButton
                color="inherit"
                onClick={() => setShowFilters(!showFilters)}
                aria-label="toggle filters"
                edge="start"
              >
                {showFilters ? <ExpandLess /> : <FilterList />}
              </IconButton>
            )}
            <Typography 
              variant={isMobile ? "subtitle1" : "h6"} 
              className={classes.title}
              style={{ 
                flex: 1,
                fontSize: isMobile ? '1rem' : '1.25rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {props.playlistName}
            </Typography>
            {/* Combined-view only: SoundCloud analysis progress (undefined for
                a Spotify-only playlist, so nothing renders). */}
            {props.combinedStatus}
            <IconButton
              edge="end"
              color="inherit"
              onClick={props.handlePlaylistClose}
              aria-label="close"
            >
              <Close />
            </IconButton>
          </Toolbar>
          
          {/* Filters always show on desktop (force-open); the collapse toggle
              only applies on mobile. Without `!isMobile`, resizing mobile→desktop
              after hiding filters left the desktop toolbar collapsed. */}
          <Collapse in={!isMobile || showFilters} timeout="auto">
            <Toolbar style={{ 
              minHeight: isMobile ? '48px' : '64px',
              padding: isMobile ? theme.spacing(0.5, 1) : theme.spacing(0, 2)
            }}>
              <Input
                classes={{
                  root: classes.search,
                  focused: classes.inputFocused,
                }}
                type={"text"}
                onChange={handleChange}
                placeholder="Search"
                style={{
                  fontSize: isMobile ? '0.875rem' : '1rem',
                  width: isMobile ? '100%' : 'auto'
                }}
                endAdornment={
                  <InputAdornment position="end">
                    <Search style={{ fontSize: isMobile ? '20px' : '24px' }} />
                  </InputAdornment>
                }
              />
            </Toolbar>
            <Toolbar 
              style={{ 
                flexDirection: 'column',
                alignItems: 'stretch',
                padding: isMobile ? theme.spacing(0.5, 1) : theme.spacing(1, 2),
                minHeight: isMobile ? 'auto' : '64px',
              }}
            >
              {/* Filter Controls */}
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: isMobile ? theme.spacing(0.5) : theme.spacing(1),
                width: '100%'
              }}>
              <FormControl className={classes.filter}>
                <InputLabel id="demo-simple-select-label">Notation</InputLabel>
                <Select
                  label="Notation"
                  labelId="demo-simple-select-label"
                  id="demo-simple-select"
                  value={wheel}
                  onChange={(e) => handleFilterChange(e, "wheel")}
                  classes={classes.select}
                  inputProps={{
                    classes: {
                      icon: classes.icon,
                      root: classes.root,
                    },
                  }}
                  MenuProps={{
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left"
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "left"
                    },
                    getContentAnchorEl: null,
                    PaperProps: {
                      style: {
                        maxHeight: isMobile ? 250 : 400,
                      }
                    }
                  }}
                  input={<Input />}
                >
                  {["Musical", "Camelot", "Open"].map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl className={classes.filter}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<DonutLarge />}
                  // Blur the trigger before opening this focus-untrapped
                  // (disableEnforceFocus) picker, so it isn't left focused
                  // inside the aria-hidden root (Chrome aria-hidden warning).
                  onClick={(e) => {
                    e.currentTarget.blur();
                    setPickerOpen(true);
                  }}
                  style={{
                    height: "100%",
                    textTransform: "none",
                    color: "#fff",
                    borderColor: "rgba(255,255,255,0.6)",
                  }}
                >
                  Filter by Key{keyFilter.length ? ` (${keyFilter.length})` : ""}
                </Button>
              </FormControl>
              <FormControl className={classes.filter}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<QueueMusic />}
                  onClick={props.onOpenSet}
                  style={{
                    height: "100%",
                    textTransform: "none",
                    color: "#fff",
                    borderColor: "rgba(255,255,255,0.6)",
                  }}
                >
                  Set{props.setCount ? ` (${props.setCount})` : ""}
                </Button>
              </FormControl>
              <FormControl className={classes.minFilter}>
                <InputLabel id="demo-simple-select-label">BPM: </InputLabel>
              </FormControl>
              <FormControl className={classes.minFilter}>
                <InputLabel id="demo-simple-select-label">Min</InputLabel>
                <Input
                  id="minBpm"
                  label="Standard"
                  type="number"
                  classes={{
                    root: classes.root,
                  }}
                  onChange={(e) => handleFilterChange(e, "minBpm")}
                />
              </FormControl>
              <FormControl className={classes.toFilter}>
                <InputLabel id="demo-simple-select-label">to</InputLabel>
              </FormControl>
              <FormControl className={classes.maxFilter}>
                <InputLabel id="demo-simple-select-label">Max</InputLabel>
                <Input
                  id="maxBpm"
                  label="Standard"
                  type="number"
                  classes={{
                    root: classes.root,
                  }}
                  // value={maxBpm}
                  onChange={(e) => handleFilterChange(e, "maxBpm")}
                />
              </FormControl>

              <FormControl className={classes.minFilter}>
                <InputLabel id="demo-simple-select-label">Year:</InputLabel>
              </FormControl>
              <FormControl className={classes.minFilter}>
                <InputLabel id="demo-simple-select-label">From</InputLabel>
                <Input
                  id="minYear"
                  type="number"
                  classes={{ root: classes.root }}
                  onChange={(e) => handleFilterChange(e, "minYear")}
                />
              </FormControl>
              <FormControl className={classes.toFilter}>
                <InputLabel id="demo-simple-select-label">to</InputLabel>
              </FormControl>
              <FormControl className={classes.maxFilter}>
                <InputLabel id="demo-simple-select-label">To</InputLabel>
                <Input
                  id="maxYear"
                  type="number"
                  classes={{ root: classes.root }}
                  onChange={(e) => handleFilterChange(e, "maxYear")}
                />
              </FormControl>

              <FormControl className={classes.filter}>
                <InputLabel id="demo-simple-select-label">Sort by</InputLabel>
                <Select
                  value={
                    sortBy === "released"
                      ? `released-${sortDir}`
                      : sortBy
                  }
                  label="Sort by"
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === "released-desc") {
                      setSortBy("released");
                      setSortDir("desc");
                    } else if (v === "released-asc") {
                      setSortBy("released");
                      setSortDir("asc");
                    } else {
                      setSortBy(v);
                      setSortDir(defaultSortDir(v));
                    }
                  }}
                  inputProps={{
                    classes: { icon: classes.icon, root: classes.root },
                  }}
                  input={<Input />}
                >
                  <MenuItem value="track">Track name</MenuItem>
                  <MenuItem value="artist">Artist</MenuItem>
                  <MenuItem value="key">Key</MenuItem>
                  <MenuItem value="bpm">BPM</MenuItem>
                  <MenuItem value="released-desc">Newest</MenuItem>
                  <MenuItem value="released-asc">Oldest</MenuItem>
                  {/* Energy/Danceability/Valence are Spotify-only features. */}
                  {!hideEnergy && <MenuItem value="energy">Energy</MenuItem>}
                  {!hideEnergy && (
                    <MenuItem value="danceability">Danceability</MenuItem>
                  )}
                  {!hideEnergy && (
                    <MenuItem value="valence">Valence (happy)</MenuItem>
                  )}
                </Select>
              </FormControl>
              {!hideEnergy && (
                <FormControl className={classes.filter}>
                  <InputLabel id="demo-simple-select-label">Energy</InputLabel>
                  <Select
                    value={energyFilter}
                    label="Energy"
                    onChange={(e) => setEnergyFilter(e.target.value)}
                    inputProps={{
                      classes: { icon: classes.icon, root: classes.root },
                    }}
                    input={<Input />}
                  >
                    <MenuItem value="any">Any energy</MenuItem>
                    <MenuItem value="low">Chill</MenuItem>
                    <MenuItem value="med">Medium</MenuItem>
                    <MenuItem value="high">Hype</MenuItem>
                  </Select>
                </FormControl>
              )}

              <IconButton
                  aria-label="clear filters"
                  className={classes.button}
                  onClick={clearFilters}
                  size="small"
                  style={{ padding: isMobile ? 4 : 8 }}
                  title="Clear all filters"
                >
                  <Delete style={{ fontSize: isMobile ? '18px' : '20px' }} />
                </IconButton>
            </div>
          </Toolbar>
          </Collapse>
        </AppBar>

        <div style={{
          // Clear the fixed bottom player (its MEASURED height) so the
          // full-width "Back To Top" bar at the end of the list is never hidden
          // behind it — for either player, any height.
          paddingBottom: `${(props.bottomInset || 0) + (isMobile ? 40 : 24)}px`,
          overflowX: isMobile ? "auto" : "visible",
          overflowY: "visible"
        }}>
          <Box style={{ padding: "8px 16px" }}>
            <Button
              size="small"
              startIcon={<Equalizer />}
              onClick={() => setShowDNA((v) => !v)}
              style={{ textTransform: "none" }}
            >
              {showDNA ? "Hide" : "Show"} Crate DNA
            </Button>
            <Collapse in={showDNA} timeout="auto" unmountOnExit>
              <CrateDNA items={allItems} getKey={getKey} />
            </Collapse>
          </Box>
          {harmonicAnchorCamelot && (
            <Box
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "10px 16px",
                backgroundColor: "#f0fbf4",
                borderBottom: "1px solid #cdeed7",
              }}
            >
              <Typography variant="body2" style={{ fontWeight: 600 }}>
                🎚 Harmonic matches for {harmonicAnchorCamelot} — compatible
                tracks highlighted, others dimmed
              </Typography>
              <Chip
                size="small"
                label="Clear"
                onClick={() => setHarmonicAnchorId(null)}
                onDelete={() => setHarmonicAnchorId(null)}
              />
            </Box>
          )}
          {sortedItems.length > 50 && (
            <TablePagination
              component="div"
              count={sortedItems.length}
              page={page}
              onChangePage={(e, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onChangeRowsPerPage={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[50, 100, 200]}
            />
          )}
          <Table>
            <TableHead ref={topRef}>
              <TableRow style={headerBg ? { background: headerBg } : undefined}>
                {!isMobile && <StyledTableCell style={headCellStyle}></StyledTableCell>}
                {!isMobile && <StyledTableCell style={headCellStyle}>Cover Art</StyledTableCell>}
                <StyledTableCell style={headCellStyle} sortDirection={sortBy === "track" ? sortDir : false}>
                  <HeadSortLabel
                    active={sortBy === "track"}
                    direction={sortBy === "track" ? sortDir : "asc"}
                    onClick={() => handleSort("track")}
                  >
                    Track
                  </HeadSortLabel>
                </StyledTableCell>
                {!isMobile && (
                  <StyledTableCell style={headCellStyle} sortDirection={sortBy === "artist" ? sortDir : false}>
                    <HeadSortLabel
                      active={sortBy === "artist"}
                      direction={sortBy === "artist" ? sortDir : "asc"}
                      onClick={() => handleSort("artist")}
                    >
                      Artist
                    </HeadSortLabel>
                  </StyledTableCell>
                )}
                <StyledTableCell style={headCellStyle} sortDirection={sortBy === "key" ? sortDir : false}>
                  <HeadSortLabel
                    active={sortBy === "key"}
                    direction={sortBy === "key" ? sortDir : "asc"}
                    onClick={() => handleSort("key")}
                  >
                    {isMobile ? "Key" : `Key (${wheel})`}
                  </HeadSortLabel>
                </StyledTableCell>
                {!isMobile && <StyledTableCell style={headCellStyle}>Quality</StyledTableCell>}
                <StyledTableCell style={headCellStyle} sortDirection={sortBy === "bpm" ? sortDir : false}>
                  <HeadSortLabel
                    active={sortBy === "bpm"}
                    direction={sortBy === "bpm" ? sortDir : "asc"}
                    onClick={() => handleSort("bpm")}
                  >
                    BPM
                  </HeadSortLabel>
                </StyledTableCell>
                {!isTablet && (
                  <StyledTableCell style={headCellStyle} sortDirection={sortBy === "released" ? sortDir : false}>
                    <HeadSortLabel
                      active={sortBy === "released"}
                      direction={sortBy === "released" ? sortDir : "asc"}
                      onClick={() => handleSort("released")}
                    >
                      Released
                    </HeadSortLabel>
                  </StyledTableCell>
                )}
                {!isTablet && !hideEnergy && (
                  <StyledTableCell style={headCellStyle} sortDirection={sortBy === "energy" ? sortDir : false}>
                    <HeadSortLabel
                      active={sortBy === "energy"}
                      direction={sortBy === "energy" ? sortDir : "asc"}
                      onClick={() => handleSort("energy")}
                    >
                      Energy
                    </HeadSortLabel>
                  </StyledTableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {pagedItems
                .map((item) => (
                  <Row
                    item={item}
                    key={item.track.id}
                    handleRowClick={handleRowClick}
                    getKey={getKey}
                    isMobile={isMobile}
                    isTablet={isTablet}
                    hideEnergy={hideEnergy}
                    wheel={wheel}
                    harmonicAnchorId={harmonicAnchorId}
                    harmonicAnchorCamelot={harmonicAnchorCamelot}
                    onToggleAnchor={toggleHarmonicAnchor}
                    onAddToSet={handleAddToSet}
                    onOverrideBpm={props.onOverrideBpm}
                    setOverride={setOverride}
                    override={overridesById[item.track.id]}
                    scStatus={
                      props.scStatusById
                        ? props.scStatusById[item.track.id]
                        : undefined
                    }
                    chords={
                      (overridesById[item.track.id] &&
                        overridesById[item.track.id].chordsOverride) ||
                      (props.chordsById && props.chordsById[item.track.id]) ||
                      spotifyChordsById[item.track.id]
                    }
                  />
                ))}
            </TableBody>
          </Table>

          {/* Bottom pagination — prev/next only (no rows-per-page). Paginates
              in place; deliberately does NOT scroll (use Back to Top for that). */}
          {sortedItems.length > rowsPerPage && (
            <Box
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 16,
                padding: "12px 16px",
              }}
            >
              <IconButton
                aria-label="previous page"
                size="small"
                disabled={page === 0}
                onClick={() => setPage(Math.max(0, page - 1))}
              >
                <ChevronLeft />
              </IconButton>
              <Typography variant="body2" style={{ color: "#666" }}>
                Page {page + 1} of {pageCount}
              </Typography>
              <IconButton
                aria-label="next page"
                size="small"
                disabled={page >= pageCount - 1}
                onClick={() => setPage(Math.min(pageCount - 1, page + 1))}
              >
                <ChevronRight />
              </IconButton>
            </Box>
          )}

          {props.userId === props.playlistOwnerId && (
            <Recommendations
              token={props.token}
              playlistId={props.playlistId}
              playlistTracks={allItems}
              playlistKeys={props.playlistKeys}
              updatePlayer={props.updatePlayer}
              addTracksToPlaylistState={props.addTracksToPlaylistState}
              seedCamelot={harmonicAnchorCamelot}
              seedBpm={anchorKey ? Math.round(anchorKey.bpm) : null}
            />
          )}

          <Fab
            variant="extended"
            style={{
              background: backToTopBg,
              color: "#FFF",
              borderRadius: "0",
              width: "100%",
              minHeight: isMobile ? "56px" : "48px",
              fontSize: isMobile ? "1rem" : "0.875rem",
            }}
            onClick={scrollToTop}
          >
            <ArrowUpward />
            Back To Top
          </Fab>
        </div>

        <KeyFilterPicker
          open={pickerOpen}
          onClose={() => setPickerOpen(false)}
          notation={wheel}
          selected={keyFilter}
          onToggle={toggleKeyFilter}
          onClear={() => setKeyFilter([])}
          filterMode={filterMode}
          onChangeFilterMode={changeFilterMode}
          bottomInset={props.bottomInset}
        />

      </Dialog>
    </div>
  );
};

export default Playlist;
